import { Code, FileText, Link as LinkIcon, NotePencil } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CONTEXT_AGGRESSIVENESS,
  CONTEXT_MAX_BYTES,
  type ContextItem,
  type ContextItemDraft,
} from "~/api/conversationTypes";
import { ApiError } from "~/api/errors";
import {
  useAddContextItem,
  useConversationContext,
  useConversationContextConfig,
  useRemoveContextItem,
  useSetContextConfig,
  useSuggestContext,
  useUpdateContextItem,
} from "~/api/queries";
import { formatBytes } from "~/features/automation/format";
import type { DocumentPorts } from "~/features/conversations/ports/editorPorts";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/** The four add paths, in the order kms offered them. Two of them need the selection port. */
const ADD_KINDS = ["doc_anchor", "code", "url", "note"] as const;
type AddKind = (typeof ADD_KINDS)[number];

const KIND_ICON = {
  doc_anchor: FileText,
  code: Code,
  url: LinkIcon,
  note: NotePencil,
} as const;

/** Verbatim type labels — the reader has to be able to tell a snippet from a note. */
const TYPE_LABEL: Record<string, string> = {
  doc_anchor: "Doc selection",
  code: "Code",
  url: "URL",
  note: "Note",
};

/**
 * `400 {"error":"context_limit_exceeded","maxBytes":102400}` is a real answer with a real
 * number in it, and rendering it as "something went wrong" throws that number away — which is
 * the difference between "delete something" and "try again forever".
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

function refusalMessage(error: unknown, t: Translate): string {
  if (error instanceof ApiError && /context_limit_exceeded/i.test(error.message)) {
    return t("The context pack is full ({{max}}). Remove an item before adding another.", {
      max: formatBytes(CONTEXT_MAX_BYTES),
    });
  }
  return error instanceof Error ? error.message : t("Something went wrong");
}

/** `https://` is retried before a URL is called invalid — people type `example.com`. */
function normaliseUrl(value: string): string | null {
  const candidate = value.trim();
  if (candidate.length === 0) {
    return null;
  }
  for (const attempt of [candidate, `https://${candidate}`]) {
    try {
      return new URL(attempt).toString();
    } catch {
      // Fall through to the prefixed attempt, then give up.
    }
  }
  return null;
}

/**
 * The context pack (W8 §B.5 / S6).
 *
 * Three sections in one tab: what the curator is allowed to do, what it has suggested, and
 * what the conversation can actually see right now. The last one is the point — a reader who
 * cannot see the pack cannot tell an answer that used the document from one that guessed.
 *
 * Two of the four add paths need a live selection, so without the editor ports they render
 * DISABLED WITH THE REASON rather than disappearing (§D.0): a missing button teaches nothing,
 * a greyed one with a sentence teaches what to do next.
 */
export function ContextPanel({
  conversationId,
  documentId,
  documentTitle,
  canComment,
  ports,
}: {
  conversationId: string;
  documentId: string;
  documentTitle: string;
  canComment: boolean;
  ports: DocumentPorts;
}) {
  const { t } = useTranslation();
  const pack = useConversationContext(conversationId);
  const config = useConversationContextConfig(conversationId);
  const add = useAddContextItem();
  const update = useUpdateContextItem();
  const remove = useRemoveContextItem();
  const setConfig = useSetContextConfig();
  const suggest = useSuggestContext();

  const [adding, setAdding] = useState<AddKind | null>(null);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const items = pack.data?.items ?? [];
  const active = items.filter((item) => (item.status || "active") === "active");
  const suggested = items.filter((item) => item.status === "suggested");
  const totalBytes = pack.data?.totalBytes ?? 0;
  const maxBytes = pack.data?.maxBytes ?? CONTEXT_MAX_BYTES;
  // `status` is CLOSED server-side: anything that is not `active` is paused.
  const autoOn = (config.data?.status ?? "paused") === "active";
  const aggressiveness = config.data?.aggressiveness ?? "balanced";

  const submit = (item: ContextItemDraft) => {
    setNotice(null);
    add.mutate(
      { id: conversationId, item },
      {
        onSuccess: () => {
          setAdding(null);
          setDraft("");
        },
        onError: (error) => setNotice(refusalMessage(error, t)),
      },
    );
  };

  const addFromSelection = (kind: "doc_anchor" | "code") => {
    const selection = ports.selection?.getSelection() ?? null;
    if (!selection) {
      setNotice(t("Select text in the document to add context"));
      return;
    }
    if (kind === "code" && !/code/i.test(selection.parentType)) {
      setNotice(t("Selection must be inside a code block"));
      return;
    }
    const language =
      typeof selection.parentAttrs.language === "string" ? selection.parentAttrs.language : "text";
    submit({
      type: kind,
      title:
        kind === "code" ? t("Code snippet ({{language}})", { language }) : t("Document selection"),
      content: selection.text,
      source: {
        documentId,
        documentTitle,
        startOffset: selection.startOffset,
        endOffset: selection.endOffset,
        contextBefore: selection.contextBefore,
        contextAfter: selection.contextAfter,
        nodeType: selection.parentType,
        language,
      },
      addedBy: "user",
    });
  };

  const selectionReason = ports.selection
    ? null
    : (ports.unavailableReason ?? t("The document isn't ready to edit."));

  return (
    <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      {/* ── What the curator may do ────────────────────────────────────────── */}
      <section className="flex flex-col gap-1">
        <Switch
          checked={autoOn}
          disabled={!canComment || setConfig.isPending}
          label={t("Context Curator")}
          description={t("Suggest-only. Nothing is added to the pack without your approval.")}
          onCheckedChange={(checked) =>
            setConfig.mutate({
              id: conversationId,
              patch: { status: checked ? "active" : "paused" },
            })
          }
        />
        <div className="flex flex-wrap gap-1" role="group" aria-label={t("How eager to suggest")}>
          {CONTEXT_AGGRESSIVENESS.map((level) => (
            <button
              key={level}
              type="button"
              aria-pressed={aggressiveness === level}
              disabled={!canComment}
              onClick={() =>
                setConfig.mutate({ id: conversationId, patch: { aggressiveness: level } })
              }
              className={cn(
                "rounded-sm px-2 py-0.5 text-caption",
                aggressiveness === level
                  ? "bg-accent text-accent-text"
                  : "bg-bg-secondary text-text-secondary hover:bg-hover-bg",
              )}
            >
              {level === "conservative"
                ? t("Conservative")
                : level === "balanced"
                  ? t("Balanced")
                  : t("Proactive")}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          className="self-start"
          disabled={!canComment || suggest.isPending}
          onClick={() =>
            suggest.mutate(
              { id: conversationId, aggressiveness },
              {
                onSuccess: (rows) =>
                  setNotice(
                    rows.length > 0
                      ? t("Added {{count}} suggestions", { count: rows.length })
                      : t("No new suggestions"),
                  ),
                onError: (error) => setNotice(refusalMessage(error, t)),
              },
            )
          }
        >
          {suggest.isPending ? t("Checking…") : t("Check for suggestions")}
        </Button>
      </section>

      {/* ── What it suggested ─────────────────────────────────────────────── */}
      <section className="flex flex-col gap-1 border-t border-divider pt-2">
        <Text variant="small" className="font-medium text-text">
          {t("Suggestions")}
        </Text>
        {suggested.length === 0 ? (
          <Text variant="small" className="text-text-tertiary">
            {t("No suggestions yet")}
          </Text>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {suggested.map((item) => (
              <li
                key={item.id}
                className="flex flex-col gap-1 rounded-md border border-divider bg-bg-secondary p-2"
              >
                <ItemHeader item={item} />
                {item.reason ? (
                  <Text variant="small" className="text-text-tertiary">
                    {item.reason}
                  </Text>
                ) : null}
                <Preview content={item.content} />
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!canComment}
                    onClick={() =>
                      update.mutate({
                        id: conversationId,
                        itemId: item.id,
                        patch: { status: "active" },
                      })
                    }
                  >
                    {t("Accept")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!canComment}
                    onClick={() =>
                      update.mutate({
                        id: conversationId,
                        itemId: item.id,
                        patch: { status: "removed" },
                      })
                    }
                  >
                    {t("Dismiss")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── What the conversation can actually see ────────────────────────── */}
      <section className="flex flex-col gap-1.5 border-t border-divider pt-2">
        <div className="flex items-center gap-2">
          <Text variant="small" className="font-medium text-text">
            {t("In context")}
          </Text>
          <Text variant="small" className="ms-auto text-text-tertiary" dir="ltr">
            {t("{{used}} of {{max}}", {
              used: formatBytes(totalBytes),
              max: formatBytes(maxBytes),
            })}
          </Text>
        </div>
        <div
          role="meter"
          aria-label={t("Context size")}
          aria-valuenow={totalBytes}
          aria-valuemin={0}
          aria-valuemax={maxBytes}
          className="h-1.5 w-full overflow-hidden rounded-full bg-bg-tertiary"
        >
          <div
            className={cn(
              "h-full",
              totalBytes / Math.max(maxBytes, 1) > 0.9 ? "bg-danger" : "bg-accent",
            )}
            style={{ inlineSize: `${Math.min(100, (totalBytes / Math.max(maxBytes, 1)) * 100)}%` }}
          />
        </div>

        {pack.isLoading ? <Spinner label={t("Loading")} /> : null}
        {!pack.isLoading && active.length === 0 ? (
          <Empty message={t("Nothing in context yet — the conversation sees only the document.")} />
        ) : null}

        <ul className="flex flex-col gap-1.5">
          {active.map((item) => (
            <li key={item.id} className="flex flex-col gap-1 rounded-md border border-divider p-2">
              <ItemHeader item={item} />
              {editingId === item.id ? (
                <>
                  <textarea
                    value={editDraft}
                    aria-label={t("Edit note")}
                    onChange={(event) => setEditDraft(event.target.value)}
                    className="min-h-20 w-full rounded-sm border border-input-border bg-bg p-2 text-body text-text"
                  />
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        update.mutate(
                          {
                            id: conversationId,
                            itemId: item.id,
                            patch: { content: editDraft },
                          },
                          {
                            onSuccess: () => setEditingId(null),
                            onError: (error) => setNotice(refusalMessage(error, t)),
                          },
                        )
                      }
                    >
                      {t("Save")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                      {t("Cancel")}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Preview content={item.content} />
                  <div className="flex gap-1">
                    {item.type === "note" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!canComment}
                        onClick={() => {
                          setEditingId(item.id);
                          setEditDraft(item.content);
                        }}
                      >
                        {t("Edit")}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!canComment}
                      onClick={() => remove.mutate({ id: conversationId, itemId: item.id })}
                      className="text-danger"
                    >
                      {t("Remove")}
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* ── Adding ────────────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-1.5 border-t border-divider pt-2">
        <Text variant="small" className="font-medium text-text">
          {t("Add context")}
        </Text>
        <div className="flex flex-wrap gap-1">
          {ADD_KINDS.map((kind) => {
            const needsSelection = kind === "doc_anchor" || kind === "code";
            return (
              <Button
                key={kind}
                size="sm"
                icon={KIND_ICON[kind]}
                disabled={
                  !canComment || add.isPending || (needsSelection && Boolean(selectionReason))
                }
                onClick={() =>
                  needsSelection
                    ? addFromSelection(kind === "code" ? "code" : "doc_anchor")
                    : setAdding(adding === kind ? null : kind)
                }
              >
                {t(TYPE_LABEL[kind] ?? "Note")}
              </Button>
            );
          })}
        </div>
        {/*
          The honest absence (§D.0). A disabled button cannot carry a tooltip — the disabled
          style removes pointer events — so the reason is written out, next to the two
          affordances it explains, instead of being hidden behind a hover nobody can trigger.
        */}
        {selectionReason ? (
          <Text variant="small" className="text-text-tertiary">
            {t("Doc selection and Code need a live selection. {{reason}}", {
              reason: selectionReason,
            })}
          </Text>
        ) : null}

        {adding === "url" ? (
          <div className="flex flex-col gap-1">
            <Input
              value={draft}
              label={t("Web address")}
              placeholder="https://example.com"
              dir="ltr"
              onChange={(event) => setDraft(event.target.value)}
            />
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  const url = normaliseUrl(draft);
                  if (!url) {
                    setNotice(draft.trim() ? t("Enter a valid URL") : t("Enter a URL"));
                    return;
                  }
                  submit({ type: "url", title: url, content: url, source: { url } });
                }}
              >
                {t("Add")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(null)}>
                {t("Cancel")}
              </Button>
            </div>
          </div>
        ) : null}

        {adding === "note" ? (
          <div className="flex flex-col gap-1">
            <textarea
              value={draft}
              aria-label={t("Note")}
              placeholder={t("Write a note…")}
              onChange={(event) => setDraft(event.target.value)}
              className="min-h-20 w-full rounded-sm border border-input-border bg-bg p-2 text-body text-text"
            />
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  const content = draft.trim();
                  if (!content) {
                    setNotice(t("Enter a note"));
                    return;
                  }
                  submit({ type: "note", title: content.slice(0, 60), content });
                }}
              >
                {t("Add")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(null)}>
                {t("Cancel")}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <div aria-live="polite" className="min-h-4 text-caption text-text-tertiary">
        {notice}
      </div>
    </div>
  );
}

function ItemHeader({ item }: { item: ContextItem }) {
  const { t } = useTranslation();
  return (
    <header className="flex items-center gap-2">
      <span className="rounded-sm bg-bg-secondary px-1.5 py-0.5 text-caption text-text-tertiary">
        {t(TYPE_LABEL[item.type] ?? "Note")}
      </span>
      <Text variant="small" truncate className="min-w-0 flex-1 text-text">
        {item.title}
      </Text>
      <span className="text-caption text-text-tertiary" dir="ltr">
        {formatBytes(item.contentBytes)}
      </span>
    </header>
  );
}

/** Four lines, then it stops — a context item is a reference, not a document. */
function Preview({ content }: { content: string }) {
  return (
    <p className="line-clamp-4 whitespace-pre-wrap text-start text-small text-text-secondary">
      {content}
    </p>
  );
}
